import { structuredPlanDesign } from '../../components/config/designFromPlan';
import { normalizeSizingSettings, resolveEquipmentMode } from './pvSizingModel';

const clone = value => JSON.parse(JSON.stringify(value));

// Summary-only legacy results must never be reverse-engineered into a new design.
export function canApplySizingDesign(row) {
  const a = row?.application, d = row?.design;
  if (row?.status !== 'designed' || !row.evaluation || a?.version !== 1 ||
      !d?.module || !d.inverter || !(d.actual_kwp > 0) ||
      !a.spacingInfo || typeof a.spacingInfo !== 'object' || Array.isArray(a.spacingInfo)) return false;
  const inverters = Object.values(a.systemDesign || {});
  if (!inverters.length || inverters.length !== d.num_inverters) return false;
  let panels = 0;
  for (const mppts of inverters) {
    if (!Array.isArray(mppts) || !mppts.length) return false;
    for (const mppt of mppts) {
      if (!Array.isArray(mppt.inputs)) return false;
      const indexes = new Set();
      const lengths = new Set();
      for (const input of mppt.inputs) {
        const n = Number(input.modules_per_string), index = Number(input.index);
        if (!Number.isInteger(n) || n <= 0 || !Number.isInteger(index) || index < 1 || indexes.has(index)) return false;
        indexes.add(index); lengths.add(n); panels += n;
      }
      // The project editor represents one string length per MPPT.
      if (lengths.size > 1) return false;
    }
  }
  return panels > 0 && panels === d.num_panels;
}

export function applySizingDesign(config, row) {
  if (!canApplySizingDesign(row)) throw new Error('PV_SIZING_APPLICATION_UNAVAILABLE');
  const { design: d, application: a } = row;
  const systemDesign = clone(a.systemDesign);
  const ids = new Set((config.orientations || [{ id: 'o1' }]).map(p => p.id));
  for (const mppts of Object.values(systemDesign)) {
    for (const mppt of mppts) {
      if (mppt.orientation && !ids.has(mppt.orientation)) throw new Error('PV_SIZING_APPLICATION_ORIENTATION');
    }
  }
  // Replace old wiring/positions rather than mixing the old plant into the new one.
  const spacingInfo = clone(a.spacingInfo);
  const pv_design = {
    ...structuredPlanDesign(systemDesign, null, d.module, d.inverter),
    capacityKw: d.actual_kwp, spacingInfo,
    sceneViews: [], obstacleShading: { stale: true },
  };
  const next = { ...config, read_module: d.module, read_inverter: d.inverter,
    pvInstalledCap: { ...config.pvInstalledCap, kw: d.actual_kwp }, systemDesign, pv_design };
  // The sizing's equipment mode, left blank, follows whether the project has
  // equipment; the design just applied gives it some. Pin the mode the run
  // used, so applying its own recommendation does not change the next run.
  if (config.pvSizing && !["project", "catalogue"].includes(config.pvSizing.equipmentMode)) {
    next.pvSizing = { ...config.pvSizing, equipmentMode: resolveEquipmentMode(normalizeSizingSettings(config.pvSizing, config), config) };
  }
  // Likewise the stage-1 reference module, which follows the project's module when unset.
  const savedReference = config.pvSizing?.reference;
  if (config.pvSizing && !["project", "generic", "generic_bifacial"].includes(savedReference?.module)) {
    next.pvSizing = { ...(next.pvSizing || config.pvSizing),
      reference: { ...(savedReference || {}), module: normalizeSizingSettings(config.pvSizing, config).reference.module } };
  }
  if (config.orientations?.length > 1) {
    next.orientations = config.orientations.map(p => ({ ...p,
      spacingInfo: clone(spacingInfo), sceneViews: [], obstacleShading: { stale: true } }));
  }
  return next;
}

export function sizingWiringSummary(systemDesign) {
  return Object.entries(systemDesign || {}).flatMap(([inverter, mppts]) =>
    Array.isArray(mppts) ? mppts.map((m, i) => ({ inverter, mppt: i + 1,
      strings: m.inputs?.length || 0,
      lengths: (m.inputs || []).map(x => x.modules_per_string).join(' / '),
      orientation: m.orientation || 'o1' })) : []);
}
